import { TGetVotes } from "@/types/votes";
import { create } from "zustand";

export type TGetProductCommentsCount = { productId: string; count: number }[];

type TGlobalStore = {
	followingParentCategories: string[];
	addFollowingParentCategory: (slug: string) => void;
	removeFollowingParentCategory: (slug: string) => void;
	setFollowingParentCategories: (categories: string[]) => void;

	followingFamilies: string[];
	addFollowingFamily: (slug: string) => void;
	removeFollowingFamily: (slug: string) => void;
	setFollowingFamilies: (categories: string[]) => void;

	followingChannels: string[];
	addFollowingChannel: (slug: string) => void;
	removeFollowingChannel: (slug: string) => void;
	setFollowingChannels: (channels: string[]) => void;

	productVotes: TGetVotes;
	addProductVote: (productId: string) => void;
	removeProductVote: (productId: string) => void;
	setProductVotes: (votes: TGetVotes) => void;

	userVotes: string[];
	addUserVote: (productId: string) => void;
	removeUserVote: (productId: string) => void;
	setUserVotes: (userVotes: string[]) => void;

	productsCommentCount: TGetProductCommentsCount;
	setProductsCommentCount: (commentsCount: TGetProductCommentsCount) => void;

	followingProfiles: string[];
	addFollowingProfile: (profile: string) => void;
	removeFollowingProfile: (profile: string) => void;
	setFollowingProfiles: (profiles: string[]) => void;
};

export const useGlobalStore = create<TGlobalStore>()((set) => ({
	// Categories Following
	followingParentCategories: [],
	addFollowingParentCategory: (slug) =>
		set((state) => ({
			followingProfiles: [...state.followingParentCategories, slug],
		})),
	removeFollowingParentCategory: (slug) =>
		set((state) => ({
			followingProfiles: state.followingParentCategories.filter((p) => p !== slug),
		})),
	setFollowingParentCategories: (categories) =>
		set(() => ({ followingParentCategories: categories })),

	// Families Following
	followingFamilies: [],
	addFollowingFamily: (slug) =>
		set((state) => ({
			followingProfiles: [...state.followingFamilies, slug],
		})),
	removeFollowingFamily: (slug) =>
		set((state) => ({
			followingProfiles: state.followingFamilies.filter((p) => p !== slug),
		})),
	setFollowingFamilies: (families) => set(() => ({ followingFamilies: families })),

	// Following Channels
	followingChannels: [],
	addFollowingChannel: (slug) =>
		set((state) => ({
			followingChannels: [...state.followingChannels, slug],
		})),
	removeFollowingChannel: (slug) =>
		set((state) => ({
			followingChannels: state.followingChannels.filter((p) => p !== slug),
		})),
	setFollowingChannels: (channels) => set(() => ({ followingChannels: channels })),

	// All the products votes count
	productVotes: [],
	addProductVote: (productId: string) =>
		set((state: TGlobalStore) => {
			const product = state.productVotes.find((product) => productId === product.productId);

			if (product) {
				const updatedProduct = {
					productId: product.productId,
					totalVotes: product.totalVotes + 1,
				};
				const updatedProducts = state.productVotes.filter((p) => p.productId !== productId);

				return {
					productVotes: [...updatedProducts, updatedProduct],
				};
			}

			return {
				productVotes: state.productVotes,
			};
		}),
	removeProductVote: (productId) =>
		set((state) => {
			const product = state.productVotes.find((product) => productId === product.productId);

			if (product) {
				const updatedProduct = {
					productId: product.productId,
					totalVotes: product.totalVotes - 1,
				};
				const updatedProducts = state.productVotes.filter((p) => p.productId !== productId);

				return {
					productVotes: [...updatedProducts, updatedProduct],
				};
			}

			return {
				productVotes: state.productVotes,
			};
		}),
	setProductVotes: (votes) => set({ productVotes: votes }),

	// User votes
	userVotes: [],
	addUserVote: (productId) => set((state) => ({ userVotes: [...state.userVotes, productId] })),
	removeUserVote: (productId) =>
		set((state) => ({
			userVotes: state.userVotes.filter((votedProductId) => votedProductId !== productId),
		})),
	setUserVotes: (userVotes) => set({ userVotes: userVotes }),

	// Product comments count
	productsCommentCount: [],
	setProductsCommentCount: (commentsCount) => set(() => ({ productsCommentCount: commentsCount })),

	// Profile Following and Followers
	followingProfiles: [],
	addFollowingProfile: (profile) =>
		set((state) => ({
			followingProfiles: [...state.followingProfiles, profile],
		})),
	removeFollowingProfile: (profile) =>
		set((state) => ({
			followingProfiles: state.followingProfiles.filter((p) => p !== profile),
		})),
	setFollowingProfiles: (profiles) => set(() => ({ followingProfiles: profiles })),
}));
